import { BoxGeometry, BufferAttribute, Matrix4, type BufferGeometry } from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import type { Cell } from './facadeAtlas';

export interface Block {
  /** Centre position and size (x across the street, z along it). */
  x: number;
  y: number;
  z: number;
  sx: number;
  sy: number;
  sz: number;
  front: Cell;
  side: Cell;
  roof: Cell;
  /** How many times the front texture repeats across and up; sides repeat sideX times across. */
  frontX: number;
  rows: number;
  sideX: number;
}

const matrix = new Matrix4();

/** Box faces in BoxGeometry order: +x, -x, +y, -y, +z, -z; 6 vertices each once non-indexed. */
function blockGeometry(block: Block): BufferGeometry {
  const box = new BoxGeometry(1, 1, 1).toNonIndexed();
  box.applyMatrix4(matrix.makeScale(block.sx, block.sy, block.sz).setPosition(block.x, block.y, block.z));
  const uv = box.getAttribute('uv') as BufferAttribute;
  const rects = new Float32Array(36 * 4);
  const faces: [Cell, number, number][] = [
    [block.front, block.frontX, block.rows],
    [block.front, block.frontX, block.rows],
    [block.roof, 1, 1],
    [block.roof, 1, 1],
    [block.side, block.sideX, block.rows],
    [block.side, block.sideX, block.rows],
  ];
  faces.forEach(([cell, rx, ry], face) => {
    for (let i = face * 6; i < face * 6 + 6; i += 1) {
      // Keep uv slightly inside [0, repeat] so fract() never lands exactly on a cell edge.
      uv.setXY(i, Math.min(uv.getX(i) * rx, rx - 0.001), Math.min(uv.getY(i) * ry, ry - 0.001));
      rects.set([cell.u, cell.v, cell.du, cell.dv], i * 4);
    }
  });
  box.setAttribute('cellRect', new BufferAttribute(rects, 4));
  box.clearGroups();
  return box;
}

/** Collects boxes for one street side and merges them into one geometry. */
export class BlockBuilder {
  private readonly blocks: BufferGeometry[] = [];

  add(block: Block): void {
    this.blocks.push(blockGeometry(block));
  }

  build(): BufferGeometry | null {
    if (this.blocks.length === 0) return null;
    const merged = mergeGeometries(this.blocks);
    this.blocks.forEach((g) => g.dispose());
    return merged;
  }
}
